import React from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, Eye, Wrench, ArrowUp, RotateCw, MoveRight } from 'lucide-react';

/**
 * RepairVisualizer - Display-only component for repair step visuals
 * Receives htmlContent and loading state from parent (RepairWizard manages cache)
 */
export const RepairVisualizer = ({
    step = {},
    stepNumber = 1,
    deviceType = 'generic',
    htmlContent = null,
    isLoading = false,
    className = ''
}) => {
    // Determine if we should show fallback visual
    const showFallback = !htmlContent && !isLoading;

    // Fallback visual (same logic as original VisualGuide)
    const FallbackVisual = () => {
        const instruction = (step?.instruction || '').toLowerCase();

        // Determine motion type and icon
        let motionType = 'focus';
        let MotionIcon = Eye;

        if (instruction.includes('rotate') || instruction.includes('turn') || instruction.includes('twist')) {
            motionType = 'rotate';
            MotionIcon = RotateCw;
        } else if (instruction.includes('pull') || instruction.includes('remove') || instruction.includes('lift')) {
            motionType = 'pull';
            MotionIcon = ArrowUp;
        } else if (instruction.includes('slide') || instruction.includes('push') || instruction.includes('insert')) {
            motionType = 'slide';
            MotionIcon = MoveRight;
        } else if (instruction.includes('replace') || instruction.includes('install') || instruction.includes('fix')) {
            motionType = 'replace';
            MotionIcon = Wrench;
        }

        // Determine target part
        let targetPart = 'component';
        const partKeywords = {
            'glass': 'Glass Pane', 'window': 'Window', 'pane': 'Glass',
            'cover': 'Cover', 'lid': 'Lid', 'panel': 'Panel',
            'screw': 'Screws', 'bolt': 'Bolts', 'nut': 'Nuts',
            'wire': 'Wiring', 'cable': 'Cable', 'connector': 'Connector',
            'motor': 'Motor', 'bearing': 'Bearing', 'fan': 'Fan',
            'screen': 'Screen', 'display': 'Display', 'button': 'Button',
            'frame': 'Frame', 'case': 'Case', 'housing': 'Housing'
        };

        for (const [keyword, part] of Object.entries(partKeywords)) {
            if (instruction.includes(keyword)) {
                targetPart = part;
                break;
            }
        }

        return (
            <div className="flex-1 flex items-center justify-center p-3 sm:p-4 relative overflow-hidden min-h-[180px] sm:min-h-[200px]">
                {/* Grid Background */}
                <div className="absolute inset-0 opacity-10"
                    style={{
                        backgroundImage: 'linear-gradient(rgba(0,240,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,240,255,0.5) 1px, transparent 1px)',
                        backgroundSize: '25px 25px'
                    }}
                />

                {/* Central Device SVG */}
                <div className="relative z-10 w-full flex justify-center">
                    <svg viewBox="0 0 160 120" className="w-full max-w-[180px] sm:max-w-[200px] h-auto">
                        {/* Device Outline */}
                        <rect x="10" y="10" width="140" height="100" rx="4"
                            fill="#1a1a1a" stroke="#333" strokeWidth="2" />

                        {/* Inner Panel */}
                        <rect x="25" y="25" width="110" height="70" rx="2"
                            fill="#252525" stroke="#444" strokeWidth="1" />

                        {/* Target Highlight */}
                        <motion.rect
                            x="40" y="35" width="80" height="50" rx="2"
                            fill="none" stroke="#FF5F1F" strokeWidth="2"
                            strokeDasharray="8 4"
                            animate={{
                                strokeDashoffset: [0, 24],
                                opacity: [0.6, 1, 0.6]
                            }}
                            transition={{ duration: 2, repeat: Infinity }}
                        />

                        {/* Pulsing Dot */}
                        <motion.circle
                            cx="80" cy="60" r="6"
                            fill="#FF5F1F"
                            animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }}
                            transition={{ duration: 1.5, repeat: Infinity }}
                        />
                    </svg>

                    {/* Animated Action Icon */}
                    <motion.div
                        className="absolute top-0 right-0 transform translate-x-2 -translate-y-2"
                        animate={
                            motionType === 'rotate' ? { rotate: [0, 360] } :
                                motionType === 'pull' ? { y: [0, -10, 0] } :
                                    motionType === 'slide' ? { x: [0, 10, 0] } :
                                        { scale: [1, 1.1, 1] }
                        }
                        transition={{ duration: 2, repeat: Infinity }}
                    >
                        <div className="bg-orange-500 p-2 rounded-full shadow-lg shadow-orange-500/40">
                            <MotionIcon className="w-5 h-5 text-white" />
                        </div>
                    </motion.div>
                </div>

                {/* Info Footer */}
                <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2 text-xs">
                    <span className="bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded font-mono uppercase">
                        {motionType}
                    </span>
                    <span className="text-gray-400">→</span>
                    <span className="text-cyan-400 font-mono">{targetPart}</span>
                </div>
            </div>
        );
    };

    return (
        <div className={`bg-black/90 border border-cyan-500/30 rounded-lg flex flex-col w-full ${className}`}
            style={{ minHeight: '250px' }}>
            {/* Header */}
            <div className="p-3 border-b border-cyan-500/20 flex items-center justify-between">
                <span className="font-mono text-xs text-cyan-400 uppercase">Visual Guide</span>
                {isLoading && (
                    <span className="flex items-center gap-1 text-xs text-cyan-400">
                        Generating...
                    </span>
                )}
                {showFallback && (
                    <span className="flex items-center gap-1 text-xs text-orange-400">
                        <AlertCircle className="w-3 h-3" />
                        Using fallback
                    </span>
                )}
            </div>

            {/* Content Area */}
            {isLoading ? (
                <div className="flex-1 flex items-center justify-center min-h-[180px] sm:min-h-[200px]">
                    <div className="generating-loader-wrapper">
                        <div className="generating-loader"></div>
                        {'GENERATING...'.split('').map((letter, index) => (
                            <span key={index} className="generating-letter">
                                {letter}
                            </span>
                        ))}
                    </div>
                </div>
            ) : showFallback ? (
                <FallbackVisual />
            ) : (
                <div className="flex-1 min-h-[180px] sm:min-h-[200px] lg:min-h-[300px]">
                    <iframe
                        sandbox="allow-scripts"
                        srcDoc={htmlContent}
                        title={`Repair Visual - Step ${stepNumber}`}
                        className="fluid-iframe"
                        style={{ background: '#0a0a0a' }}
                    />
                </div>
            )}
        </div>
    );
};

export default RepairVisualizer;
